import { useState } from "react";
import CustomizedTables, { StyledTableCell, StyledTableRow } from "../../../Component/UI/Table/Table";
import classes from "../homePageNew.module.css";
import { DownloadIcon, EyeIcon, OpenIcon, TrashIcon } from "./icons";
import { reportDownloadUrl, openReportInNewTab, mapLlmLabels } from "../api/homePageApi";
import { downloadFile } from "../../../BrandOnBoarding/api";
import OverflowPill from "./OverflowPill";
import PaginationBar from "./PaginationBar";
import ReportStatusChip from "./ReportStatusChip";

const PAGE_SIZE = 5;

const BRAND_HEADER = { id: "brand", name: "Brand" };
const REST_HEADERS = [
  { id: "type", name: "Type" },
  { id: "category", name: "Category" },
  { id: "llm", name: "LLM" },
  { id: "market", name: "Market" },
  { id: "persona", name: "Persona" },
  { id: "date", name: "Date" },
  { id: "status", name: "Status" },
  { id: "actions", name: "Actions" },
];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "2-digit" });
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Generic report history table — same table shell
 * (Component/UI/Table/Table.js's CustomizedTables) and the same
 * Brand/Category/LLM/Market/Persona/Date/Status/Actions design
 * DiscoveryV2/Reporting/DiscoveryReportingModal.js's own report history uses
 * (view/open/download/delete, badge-styled multi-value cells, client-side
 * pagination), minus its "Generate JSON"/"Generate New Version" triggers —
 * those stay with whatever screen opens this. `brand` is constant across
 * every row here (the history is already scoped to one record), matching
 * how that same column reads in the real modal. Any future module's report
 * list can reuse this directly by passing its own rows.
 *
 * `showBrand` (default true) drops the Brand column entirely — Category
 * Analysis has no main brand (it's a competitive leaderboard across several
 * competitor brands, not one brand's own report), so that column would only
 * ever read "—" there.
 */
export default function ReportHistoryTable({ rows, brand, onView, onDelete, showBrand = true }) {
  const [page, setPage] = useState(1);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE;
  const pageRows = rows.slice(start, start + PAGE_SIZE);
  const headers = showBrand ? [BRAND_HEADER, ...REST_HEADERS] : REST_HEADERS;

  return (
    <>
      <CustomizedTables
        headers={headers}
        data={pageRows}
        render={(row) => (
          <StyledTableRow key={row.jobId}>
            {showBrand && (
              <StyledTableCell>
                <span className={classes.reportBadge}>{row.brand || brand || "—"}</span>
              </StyledTableCell>
            )}
            <StyledTableCell>
              <span className={classes.reportBadge}>{row.type || "—"}</span>
            </StyledTableCell>
            <StyledTableCell>
              <span className={classes.reportBadge}>{row.category || "—"}</span>
            </StyledTableCell>
            <StyledTableCell>
              <OverflowPill values={mapLlmLabels(row.llms)} label="Models" mono />
            </StyledTableCell>
            <StyledTableCell>
              <OverflowPill values={row.markets} label="Markets" />
            </StyledTableCell>
            <StyledTableCell>
              <OverflowPill values={row.persona?.length ? row.persona : ["General Profile"]} label="Personas" />
            </StyledTableCell>
            <StyledTableCell title={formatDateTime(row.date)}>{formatDate(row.date)}</StyledTableCell>
            <StyledTableCell>
              <ReportStatusChip status={row.status} />
            </StyledTableCell>
            <StyledTableCell>
              <div style={{ display: "flex", gap: 10, cursor: "pointer" }}>
                <span onClick={() => onView(row)} title="View report">
                  <EyeIcon size={15} />
                </span>
                <span onClick={() => openReportInNewTab(row.jobId)} title="Open in new tab">
                  <OpenIcon size={15} />
                </span>
                <span
                  onClick={() => downloadFile(reportDownloadUrl(row.jobId), `Report ${row.jobId}.html`)}
                  title="Download"
                >
                  <DownloadIcon size={15} />
                </span>
                <span onClick={() => onDelete(row)} title="Delete report">
                  <TrashIcon size={15} />
                </span>
              </div>
            </StyledTableCell>
          </StyledTableRow>
        )}
      />
      {totalPages > 1 && (
        <div className={classes.reportHistoryPager}>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            total={rows.length}
            start={start}
            count={pageRows.length}
            onPageChange={setPage}
          />
        </div>
      )}
    </>
  );
}
